import type { Spacing } from '@iheartjane/reefer';
import { useCallback } from 'react';

import { useIpadMediaQuery } from './useIpadMediaQuery';
import type { IpadOrientation } from './useIpadMediaQuery';

type UseKioskSpacingParams = {
  defaultPadSize?: Spacing;
  defaultSize?: Spacing;
};

type UseKioskSpacingReturn = {
  /** From `defaultPadSize` & `defaultSize`, this is the default size for the current screen. */
  defaultSpacing: Spacing;
  /** Defaulting to `defaultPadSize` & `defaultSize`, returns the size for the current screen. */
  getKioskSpacing: (size?: Spacing, padSize?: Spacing) => Spacing;
  ipadOrientation: IpadOrientation;
  /** The result of `useIpadMediaQuery`; whether the current screen is iPad sized. */
  isIpad: boolean;
};

/**
 * @param [defaultSizing.defaultPadSize] - the default size for tablets
 * @param [defaultSizing.defaultSize] - the default size for larger screens
 */
export const useKioskSpacing = (
  defaultSizing: UseKioskSpacingParams = {}
): UseKioskSpacingReturn => {
  const { defaultPadSize = 24, defaultSize = 32 } = defaultSizing;
  const ipadOrientation = useIpadMediaQuery();

  const getKioskSpacing = useCallback(
    (size: Spacing = defaultSize, padSize: Spacing = defaultPadSize) => {
      return ipadOrientation ? padSize : size;
    },
    [defaultPadSize, defaultSize, ipadOrientation]
  );

  return {
    defaultSpacing: ipadOrientation ? defaultPadSize : defaultSize,
    getKioskSpacing,
    ipadOrientation,
    isIpad: Boolean(ipadOrientation),
  };
};
